import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClassroomTreeNode } from '../single-classroom/single-classroom.component';

export interface ClassroomSelection {
    classroomId: number;
    classroomName: string;
    /** Course node id (when the tree parent carries a value) */
    courseId: number | null;
    courseName: string;
}

/**
 * Multi-select dropdown tree for the classroom hierarchy (course → section).
 * Same data shape as `app-single-classroom`; leaves carry checkboxes, a
 * branch checkbox toggles all its sections at once. Emits the full
 * selection (with parent course info) on every change.
 */
@Component({
    selector: 'app-multi-classroom',
    imports: [CommonModule, FormsModule],
    templateUrl: './multi-classroom.component.html',
    styleUrl: './multi-classroom.component.scss'
})
export class MultiClassroomComponent implements OnChanges {

    @Input() data: ClassroomTreeNode[] | null = null;
    @Input() value: number[] = [];
    @Input() placeholder = 'Select class / sections';
    @Output() valueChange = new EventEmitter<number[]>();
    @Output() selectionChange = new EventEmitter<ClassroomSelection[]>();

    open = false;
    filterText = '';
    expanded = new Set<ClassroomTreeNode>();
    selected = new Set<number>();

    ngOnChanges(): void {
        this.selected = new Set((this.value || []).map(v => Number(v)));
    }

    get buttonLabel(): string {
        if (!this.selected.size) { return this.placeholder; }
        const names = this.currentSelection()
            .map(s => s.courseName ? `${s.courseName} · ${s.classroomName}` : s.classroomName);
        if (names.length <= 2) { return names.join(', '); }
        return `${names[0]}, ${names[1]} +${names.length - 2} more`;
    }

    get filteredNodes(): ClassroomTreeNode[] {
        const q = this.filterText.trim().toLowerCase();
        if (!q) return this.data || [];
        return this.filterTree(this.data || [], q);
    }

    private filterTree(nodes: ClassroomTreeNode[], q: string): ClassroomTreeNode[] {
        const out: ClassroomTreeNode[] = [];
        for (const node of nodes) {
            const selfMatch = (node.text || '').toLowerCase().includes(q);
            const children = node.children?.length ? this.filterTree(node.children, q) : [];
            if (selfMatch && (!node.children || !node.children.length)) {
                out.push(node);
            } else if (children.length) {
                out.push({ ...node, children });
            } else if (selfMatch) {
                out.push(node);
            }
        }
        return out;
    }

    isLeaf(node: ClassroomTreeNode): boolean {
        return !node.children || node.children.length === 0;
    }

    isExpanded(node: ClassroomTreeNode): boolean {
        return !!this.filterText.trim() || this.expanded.has(node);
    }

    leafId(node: ClassroomTreeNode): number {
        return Number(node.value?.value ?? node.value);
    }

    isChecked(node: ClassroomTreeNode): boolean {
        return this.selected.has(this.leafId(node));
    }

    /** Branch state: all / some / none of the descendant leaves selected. */
    branchState(node: ClassroomTreeNode): 'all' | 'some' | 'none' {
        const leaves = this.collectLeaves(node);
        if (!leaves.length) { return 'none'; }
        const on = leaves.filter(l => this.selected.has(this.leafId(l))).length;
        if (on === 0) { return 'none'; }
        return on === leaves.length ? 'all' : 'some';
    }

    private collectLeaves(node: ClassroomTreeNode): ClassroomTreeNode[] {
        if (this.isLeaf(node)) { return [node]; }
        const out: ClassroomTreeNode[] = [];
        (node.children || []).forEach(c => out.push(...this.collectLeaves(c)));
        return out;
    }

    toggleBranch(node: ClassroomTreeNode, event: Event): void {
        event.stopPropagation();
        const leaves = this.collectLeaves(node);
        const allOn = this.branchState(node) === 'all';
        leaves.forEach(l => {
            const id = this.leafId(l);
            if (Number.isNaN(id)) { return; }
            if (allOn) { this.selected.delete(id); } else { this.selected.add(id); }
        });
        this.emit();
    }

    toggleExpand(node: ClassroomTreeNode, event: Event): void {
        event.stopPropagation();
        if (this.expanded.has(node)) {
            this.expanded.delete(node);
        } else {
            this.expanded.add(node);
        }
    }

    toggleLeaf(node: ClassroomTreeNode, event: Event): void {
        event.stopPropagation();
        const id = this.leafId(node);
        if (Number.isNaN(id)) { return; }
        if (this.selected.has(id)) {
            this.selected.delete(id);
        } else {
            this.selected.add(id);
        }
        this.emit();
    }

    clearAll(event: Event): void {
        event.stopPropagation();
        this.selected.clear();
        this.emit();
    }

    private emit(): void {
        this.value = Array.from(this.selected);
        this.valueChange.emit(this.value);
        this.selectionChange.emit(this.currentSelection());
    }

    currentSelection(): ClassroomSelection[] {
        const out: ClassroomSelection[] = [];
        this.walk(this.data || [], null, null, out);
        return out;
    }

    private walk(nodes: ClassroomTreeNode[], courseId: number | null,
                 courseName: string | null, out: ClassroomSelection[]): void {
        for (const node of nodes) {
            if (this.isLeaf(node)) {
                const id = this.leafId(node);
                if (this.selected.has(id)) {
                    out.push({
                        classroomId: id,
                        classroomName: node.text || '',
                        courseId,
                        courseName: courseName || ''
                    });
                }
            } else {
                const branchId = Number(node.value?.value ?? node.value);
                this.walk(node.children || [], Number.isNaN(branchId) ? courseId : branchId,
                    node.text || courseName, out);
            }
        }
    }

    toggleOpen(event: Event): void {
        event.stopPropagation();
        this.open = !this.open;
        if (this.open) this.filterText = '';
    }

    @HostListener('document:click')
    close(): void {
        this.open = false;
    }
}
